import { InviteData, useRegisterUser } from 'api/register';
import { LayoutVariants } from 'components/layout/UnauthorisedLayout/UnauthorisedLayout';
import Form, { FormOnSubmit } from 'components/ui/Form/Form/Form';
import Radio from 'components/ui/Form/Radio/Radio';
import AmnisButton from 'components/ui/AmnisButton/AmnisButton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isCountry, useCountries } from 'api/countries';
import qs from 'qs';
import { useLocation } from 'react-router';
import {
    getPrivacyPolicy,
    getTermsAndConditions,
} from 'helpers/termsAndPrivacy';
import { useCookies } from 'react-cookie';
import useAnalytics from 'hooks/useAnalytics';
import { useLanguage } from 'context/language-context';
import { useAuth } from 'context/auth-context';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useResolveInviteStore } from 'components/pages/ResolveInvitePage/resolveInviteStore';
import { PATH } from 'constants/common';
import { handleTypedFormError } from 'helpers/handleFormError';
import { REGISTRATION_VARIANT_MAP } from 'components/pages/RegistrationPage/RegistrationPage';
import { registerFormValidationSchema } from 'form/validation/register';
import styles from './RegistrationPage.module.scss';
import TextInput from 'components/ui/Form/TextInput/TextInput';
import BusinessEmailInput from 'components/ui/Form/BusinessEmailInput/BusinessEmailInput';
import PhoneInput from 'components/ui/Form/PhoneInput/PhoneInput';
import CountryAutoComplete from 'components/ui/Form/AutoComplete/CountryAutoComplete';
import Alert from 'components/ui/Alert/Alert';
import Checkbox from 'components/ui/Form/Checkbox/Checkbox';
import MaintenanceMessage from 'components/ui/MaintenanceMessage/MaintenanceMessage';
import { Link } from 'react-router-dom';
import CustomFormError from 'components/ui/Form/CustomFormError/CustomFormError';
import { Alert as BootstrapAlert } from 'react-bootstrap';
import { useFingerprintStore } from 'stores/fingerprintStore';
import { isCardRegistration } from 'helpers/common';

export interface IRegisterForm {
    gender: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    plainPassword: string;
    acceptTerms: boolean;
    businessCountry?: string | null;
}

const genders = [
    { label: 'Mrs', value: '2' },
    { label: 'Mr', value: '1' },
];

const getButtonText = (variant: LayoutVariants, inviteDate?: InviteData) => {
    if (
        inviteDate?.['@type'] === 'BusinessPartnerInvite' ||
        variant === LayoutVariants.WIENER_PRIVATBANK ||
        variant === LayoutVariants.TWENTYFOUR_SEVEN ||
        variant === LayoutVariants.CARD
    ) {
        return 'Register now';
    }
    if (variant === LayoutVariants.BEXIO) {
        return 'Create your account';
    }
    return 'Create Demo Account';
};

interface IProps {
    variant: LayoutVariants;
    inviteData?: InviteData;
}

const getPhoneFieldLabel = (
    variant: LayoutVariants,
    inviteData?: InviteData,
) => {
    if (variant === LayoutVariants.WIENER_PRIVATBANK) {
        return 'Mobile phone number';
    }

    return isCardRegistration(inviteData)
        ? 'Phone Number'
        : 'Business Phone Number';
};

const getBusinessCountryLabel = (variant: LayoutVariants) => {
    return variant === LayoutVariants.WIENER_PRIVATBANK
        ? 'Registration country'
        : 'Company registration country';
};

const RegisterForm = ({ variant, inviteData }: IProps) => {
    const inviteType = useResolveInviteStore((state) => state.type);
    const hash = useResolveInviteStore((state) => state.hash);
    const { selectedLanguageParams } = useLanguage();
    const location = useLocation();
    const params = qs.parse(location.search, {
        ignoreQueryPrefix: true,
        decoder(str: string, defaultDecoder: (str: string) => string) {
            const replaced = str.replace(/\+/g, '%2B');
            return defaultDecoder(replaced);
        },
    });
    const [invalidCountry, setInvalidCountry] = useState<boolean>(false);
    const { countries, isLoading: isCountriesLoading } = useCountries();
    const [cookies] = useCookies();
    const [recaptchaLoading, setRecaptchaLoading] = useState<boolean>(false);
    const { addEvent } = useAnalytics();
    const fingerprintRequestId = useFingerprintStore(
        (state) => state.fingerprintRequestId,
    );
    const isFingerprintLoading = useFingerprintStore(
        (state) => state.isLoading,
    );
    const getFingerprintData = useFingerprintStore(
        (state) => state.getFingerprintData,
    );

    const { selectedLanguageId } = useLanguage();
    const { login } = useAuth();

    const { executeRecaptcha } = useGoogleReCaptcha();
    const { mutate: registerUser, isPending: isLoading } = useRegisterUser();

    const getPrivacyPoliciyUrl = useCallback(
        () => getPrivacyPolicy(selectedLanguageParams.code),
        [selectedLanguageParams.code],
    );

    const getTermsAndConditionsUrl = useCallback(
        () => getTermsAndConditions(selectedLanguageParams.code),
        [selectedLanguageParams.code],
    );

    useEffect(() => {
        getFingerprintData();
    }, [getFingerprintData]);

    const onSubmit: FormOnSubmit<IRegisterForm> = async (values, setError) => {
        let recaptchaToken: string | undefined;
        try {
            setRecaptchaLoading(true);
            recaptchaToken = await executeRecaptcha?.('registration');
        } finally {
            setRecaptchaLoading(false);
        }
        const { acceptTerms, ...rest } = values;

        const campaignUrl = cookies.__gtm_campaign_url;
        let gclid: string | null = null;
        if (campaignUrl) {
            try {
                const parsed = new URLSearchParams(
                    new URL(decodeURIComponent(campaignUrl)).search,
                );
                gclid = parsed.get('gclid') || null;
            } catch {
                gclid = null;
            }
        }

        registerUser(
            {
                requestData: {
                    ...rest,
                    fingerprintRequestId,
                    WIRCustomer:
                        variant === LayoutVariants.WIR ? true : undefined,

                    businessPartnerInviteHash:
                        inviteType === 'business-partner' ? hash! : undefined,
                    contactInviteHash:
                        inviteType === 'contact' ? hash! : undefined,

                    // bexio fields
                    bexioId: params?.bexioId?.toString() || undefined,
                    registrationRequestId:
                        params?.registrationRequestId?.toString() || undefined,

                    referringPartner:
                        location.pathname === PATH.REGISTRATION_RATE_ALERT
                            ? 'rate_alert'
                            : REGISTRATION_VARIANT_MAP[variant]
                                  .referringPartner,
                    metadata: {
                        utm: [
                            cookies.__gtm_referrer,
                            cookies.__gtm_campaign_url,
                        ],
                        gclid,
                    },
                },
                currentLanguageId: selectedLanguageId,
                recaptchaToken,
            },
            {
                onSuccess: (success) => {
                    addEvent('app_demo_registration');

                    login(
                        success.token,
                        location.pathname === PATH.REGISTRATION_RATE_ALERT
                            ? PATH.RATE_ALERT
                            : undefined,
                    );
                    useResolveInviteStore.persist.clearStorage();
                },
                onError: (requestErrors: any) =>
                    handleTypedFormError(requestErrors, setError, values),
            },
        );
    };

    const defaultFormValues = useMemo(() => {
        let gender = '';
        if (params.userGender) {
            gender = params.userGender === '0' ? '1' : '2';
        }

        if (inviteData && inviteData['@type'] === 'BusinessPartnerInvite') {
            gender = inviteData.gender === 'male' ? '1' : '2';
            return {
                firstName: inviteData.firstName,
                lastName: inviteData.lastName,
                email: inviteData.email,
                gender,
                businessCountry: null,
            };
        }
        return {
            firstName: `${params.userFirstName ?? ''}`,
            lastName: `${params.userLastName ?? ''}`,
            gender,
            email: `${params.userEmail ?? ''}`,
            businessCountry: null,
        };
    }, [inviteData, params]);

    return (
        <Form<IRegisterForm>
            onSubmit={onSubmit}
            validationSchema={registerFormValidationSchema(
                !!hash || location.pathname === PATH.REGISTRATION_CARD,
            )}
            defaultValues={defaultFormValues}
            className={styles.form}
        >
            <Radio
                label="Choose your title"
                name="gender"
                options={genders}
                horizontal
            />
            <div className={styles.nameInputs}>
                <TextInput
                    label="First Name(s)"
                    name="firstName"
                    info="Please provide all your first names according to your passport"
                    size="large"
                />
                <TextInput label="Last Name" name="lastName" size="large" />
            </div>
            {isCardRegistration(inviteData) ? (
                <>
                    <TextInput
                        label="Email"
                        name="email"
                        readOnly={defaultFormValues.email !== ''}
                        size="large"
                    />
                    {defaultFormValues.email === '' ? (
                        <Alert
                            message="Important: Use the same email address your company used to invite you. This ensures your account is correctly linked to your card. If unsure, please check with your administrator."
                            variant="info"
                        />
                    ) : null}
                </>
            ) : (
                <BusinessEmailInput
                    label="Business Email"
                    name="email"
                    readOnly={inviteData?.['@type'] === 'BusinessPartnerInvite'}
                    size="large"
                />
            )}
            <PhoneInput
                name="phone"
                label={getPhoneFieldLabel(variant, inviteData)}
                size="large"
            />
            {inviteData?.['@type'] === 'BusinessPartnerInvite' ||
            location.pathname === PATH.REGISTRATION_CARD ? null : (
                <CountryAutoComplete
                    name="businessCountry"
                    isLoading={isCountriesLoading}
                    label={getBusinessCountryLabel(variant)}
                    placeholder=""
                    options={countries}
                    onChange={(value) => {
                        if (!isCountry(value)) {
                            return;
                        }

                        setInvalidCountry(!value.onboardingCorporate);
                    }}
                    size="large"
                />
            )}
            {invalidCountry ? (
                <Alert
                    message="In line with our anti-money laundering policy and sanction screening requirements, we cannot provide our service to companies registered in your country. Sorry for any inconvenience this may cause"
                    variant="danger"
                />
            ) : null}
            <TextInput
                label="Password"
                name="plainPassword"
                type="password"
                autoComplete="current-password"
                size="large"
            />
            <Checkbox
                name="acceptTerms"
                afterLabel={
                    <span data-isolate>
                        By submitting, you consent that you have read and agree
                        to our&nbsp;
                        <a
                            target="_blank"
                            rel="noreferrer noopener"
                            href={getTermsAndConditionsUrl()}
                        >
                            Terms and policies
                        </a>
                        &nbsp;and&nbsp;
                        <a
                            target="_blank"
                            rel="noreferrer noopener"
                            href={getPrivacyPoliciyUrl()}
                        >
                            Privacy Policy
                        </a>
                    </span>
                }
            />
            <MaintenanceMessage />
            <AmnisButton
                type="submit"
                isLoading={
                    isLoading || recaptchaLoading || isFingerprintLoading
                }
                disabled={
                    invalidCountry || window.__env__.MAINTENANCE_ACTIVE === '1'
                }
            >
                {getButtonText(variant, inviteData)}
            </AmnisButton>
            <div className={styles.login}>
                Already a member?
                <Link to={REGISTRATION_VARIANT_MAP[variant].loginPath}>
                    Login
                </Link>
            </div>
            <CustomFormError errorKey="userExists">
                <BootstrapAlert
                    variant="danger"
                    style={{ textAlign: 'center' }}
                >
                    Email is already in use. Please{' '}
                    <span style={{ fontWeight: 'bold' }}>
                        <Link to="/login">Login.</Link>
                    </span>
                </BootstrapAlert>
            </CustomFormError>
        </Form>
    );
};

export default RegisterForm;
